<script>
/**
 * ****************************************************************************
 * @description     :   Select Component
 * @created at      :   2024/05/22
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */
/**
 * Import template
 */
import template from './template.html';
import helpers from '@/utils/helpers';
// import helpers from '@/utils/helpers';

/**
 * Define content of select component
 */
var AnsSelectSearch = {
    template: template,
    props: {
        id: String,
        label: String,
        name: String,
        itemClass: {
            type: [String, Object],
            default: ''
        },
        labelClass: String,
        inputClass: [String, Object],
        dropdownClass: {
            type: [String, Object],
            default: ''
        },
        value: [String, Number],
        modelValue: [String, Number],
        options: {
            type: Array,
            default: () => []
        },
        keyValueOptions: {
            type: Array,
            default: () => ['code', 'name'] // [valueKey, labelKey]
        },
        avatarKey: {
            type: String,
            default: 'avatar' // key avatar trong option
        },
        placeholder: {
            type: String,
            default: ''
        },
        emptyText: {
            type: String,
            default: 'データがありません'
        },
        isRequired: Boolean,
        isDisabled: Boolean,
        isReadonly: Boolean,
        isMobile: {
            type: Boolean,
            default: false
        },
        isHorizontal: {
            type: Boolean,
            default: false
        },
        widthLabel: {
            type: String,
            default: 'width-110-px'
        },
        errors: Array,
        isAlwaysEnable: {
            type: Boolean,
            default: false
        },
        tabindex: {
            type: Number,
            default: 1
        },
        clearable: {
            type: Boolean,
            default: true
        },
        onChange: Function
    },
    emits: ['update:modelValue'],
    data() {
        return {
            modeDisabled: false,
            isOpen: false,
            searchText: '',
            highlightedIndex: -1
        };
    },
    created() {
        if (
            ![
                'MNG_Order001',
                'MNG_Order004',
                'MNG_System002',
                'MNG_System004',
                'MNG_File001',
                'CUS_Order021',
                'CUS_Billing001',
                'CUS_Client003',
                'CUS_Delivery001'
            ].includes(this.$route.name)
        ) {
            this.modeDisabled = helpers.checkAuthorityMode(this.$route.name) && !this.isAlwaysEnable;
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    computed: {
        vewValue() {
            return this.modelValue != null && this.modelValue !== '' ? this.modelValue : this.value;
        },
        valueKey() {
            return this.keyValueOptions[0];
        },
        labelKey() {
            return this.keyValueOptions[1];
        },
        selectedOption() {
            return this.options.find((o) => o[this.valueKey] == this.vewValue) || null;
        },
        displayLabel() {
            // Khi không mở dropdown -> hiển thị label đã chọn
            if (!this.isOpen && this.selectedOption) {
                return this.selectedOption[this.labelKey];
            }
            // Khi đang mở dropdown -> hiển thị searchText
            return this.searchText;
        },
        filteredOptions() {
            const kw = (this.searchText || '').toLowerCase();
            if (!kw) return this.options;
            return this.options.filter((o) => (o[this.labelKey] || '').toString().toLowerCase().includes(kw));
        },
        wrapperClass() {
            const base = {
                'd-flex': true,
                'item-component': true,
                'position-relative': true
            };
            return { ...base, ...this.itemClass };
        },
        inputClassComputed() {
            const base = {
                'form-control': true,
                'custom-input': true,
                'rounded-025': true,
                'font-size-12-px': true,
                'border-1': true,
                'cursor-pointer': !this.isDisabled && !this.modeDisabled,
                'bg-white': true
            };
            if (typeof this.inputClass === 'object' && this.inputClass !== null) {
                return { ...base, ...this.inputClass };
            } else if (typeof this.inputClass === 'string' && this.inputClass) {
                return { ...base, [this.inputClass]: true };
            }
            return base;
        },
        dropdownClassComputed() {
            const base = {
                'ans-select-search-dropdown': true
            };
            if (typeof this.dropdownClass === 'object' && this.dropdownClass !== null) {
                return { ...base, ...this.dropdownClass };
            } else if (typeof this.dropdownClass === 'string' && this.dropdownClass) {
                return { ...base, [this.dropdownClass]: true };
            }
            return base;
        }
    },
    methods: {
        handleClickOutside(e) {
            const root = this.$el;
            if (!root) return;
            if (!root.contains(e.target)) {
                this.closeDropdown();
            }
        },
        openDropdown() {
            if (this.isDisabled || this.modeDisabled || this.isReadonly) return;

            this.isOpen = true;
            this.searchText = '';
            this.highlightedIndex = -1;

            this.$nextTick(() => {
                const root = this.$el;
                const rect = root.getBoundingClientRect();

                const spaceBelow = window.innerHeight - rect.bottom;
                const dropdownHeight = 240; // chiều cao tối đa dropdown của bạn

                // Nếu khoảng dưới không đủ → mở lên
                if (spaceBelow < dropdownHeight) {
                    root.classList.add('dropdown-open-up');
                } else {
                    root.classList.remove('dropdown-open-up');
                }

                const input = this.$refs.searchInput;
                input && input.focus();
            });
        },
        closeDropdown() {
            this.isOpen = false;
            this.searchText = '';
            this.highlightedIndex = -1;
        },
        toggleDropdown() {
            if (this.isOpen) this.closeDropdown();
            else this.openDropdown();
        },
        onSearchInput(e) {
            this.searchText = e.target.value;
            if (!this.isOpen) this.isOpen = true;
            if (this.filteredOptions.length > 0) {
                this.highlightedIndex = 0;
            } else {
                this.highlightedIndex = -1;
            }
        },
        onSearchKeydown(e) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!this.filteredOptions.length) return;
                this.highlightedIndex = (this.highlightedIndex + 1 + this.filteredOptions.length) % this.filteredOptions.length;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (!this.filteredOptions.length) return;
                this.highlightedIndex = (this.highlightedIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (this.highlightedIndex >= 0) {
                    const opt = this.filteredOptions[this.highlightedIndex];
                    this.selectOption(opt);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.closeDropdown();
            }
        },
        selectOption(option) {
            const v = option[this.valueKey];
            this.$emit('update:modelValue', v);
            if (this.onChange) {
                this.onChange(v);
            }
            this.closeDropdown();
        },
        clearSelection(e) {
            e.stopPropagation();
            this.$emit('update:modelValue', '');
            if (this.onChange) {
                this.onChange('');
            }
        }
    }
};

export default AnsSelectSearch;
</script>
<style>
.ans-select-search-wrapper {
    height: 30px;
    border: 1px solid black;
    border-radius: 0;
    padding: 2px 6px;
    display: flex;
    align-items: center;
    cursor: pointer;
    background-color: white;
    padding-left: 10px;
}

.ans-select-search-selected {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    justify-content: space-between;
}

.ans-select-search-placeholder {
    color: #9ca3af;
    font-size: 12px;
}

.ans-select-search-avatar {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    object-fit: cover;
}

.ans-select-search-selected-label {
    flex: 1; /* cho phép chiếm phần còn lại */
    min-width: 0; /* bắt buộc với flex để ellipsis hoạt động */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap; /* 1 dòng, nếu muốn vẫn xuống dòng thì bỏ dòng này */
    font-size: 12px;
}

.ans-select-search-clear {
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    padding: 0 4px;
    font-size: 17px;
}

.ans-select-search-dropdown {
    position: absolute;
    z-index: 30;
    left: 0;
    right: 0;
    margin-top: 2px;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.15);
    font-size: 12px;
}

.ans-select-search-box {
    position: relative;
    padding: 4px;
    border-bottom: 1px solid #e5e7eb;
}

.ans-select-search-input {
    padding-left: 24px;
    font-size: 12px;
}

.ans-select-search-icon {
    position: absolute;
    top: 50%;
    left: 10px;
    transform: translateY(-50%);
    font-size: 12px;
    color: #9ca3af;
}

.ans-select-search-list {
    max-height: 220px;
    overflow-y: auto;
}

.ans-select-search-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    cursor: pointer;
}
.ans-select-search-item:hover {
    background-color: #6495ed !important;
    color: white;
}

.ans-select-search-item.is-selected {
    background-color: #6495ed !important;
    color: white;
}

.ans-select-search-item.is-highlighted {
    background-color: #d1fae5;
}

.ans-select-search-name {
    font-size: 12px;
}

.ans-select-search-empty {
    padding: 8px 10px;
    color: #6b7280;
}
/* Dropdown mặc định mở xuống */
.ans-select-search-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 2px);
    z-index: 999;
}

/* Khi mở lên trên */
.dropdown-open-up .ans-select-search-dropdown {
    bottom: calc(100% + 2px);
    top: auto !important; /* override để không mở xuống */
}
</style>
